// Relative timestamp tinted by staleness, so freshness reads at a glance.
//
// Generalised from LastInteraction in routes/Search.tsx, which uses day-scale
// thresholds for a ministering visit queue. An incident board needs the same
// affordance at minute scale — an address swept four hours ago has to look
// different from one swept ten minutes ago.

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export interface Thresholds {
  /** Below this many ms: fresh (green). */
  fresh: number;
  /** Below this many ms: aging (amber). Beyond it: stale (rose). */
  aging: number;
}

/** Ministering cadence — the original Search.tsx behaviour. */
export const DAY_SCALE: Thresholds = { fresh: 30 * DAY, aging: 90 * DAY };
/** Incident sweep cadence. */
export const SWEEP_SCALE: Thresholds = { fresh: 30 * MINUTE, aging: 4 * HOUR };

export function formatAgo(ms: number): string {
  if (ms < MINUTE) return 'just now';
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)}m ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h ago`;
  const days = Math.floor(ms / DAY);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function RelativeTime({
  at,
  thresholds = SWEEP_SCALE,
  never = 'never',
}: {
  at: string | null;
  thresholds?: Thresholds;
  never?: string;
}) {
  if (!at) {
    return <span className="text-xs text-slate-400 dark:text-slate-500">{never}</span>;
  }
  const ms = Date.now() - new Date(at).getTime();
  const tone =
    ms <= thresholds.fresh
      ? 'text-emerald-700 dark:text-emerald-300'
      : ms <= thresholds.aging
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-rose-700 dark:text-rose-300';
  return (
    <span className={`text-xs tabular-nums ${tone}`} title={new Date(at).toLocaleString()}>
      {formatAgo(ms)}
    </span>
  );
}
